<template>
  <client-only>

    <div v-if="isLoading" class="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <core-upsell-product-card-skeleton />
      <core-upsell-product-card-skeleton />
      <core-upsell-product-card-skeleton />
    </div>

    <core-contextual-error-box v-else-if="fetchError" size="sm" class="border-2 border-gray-200 my-3 rounded-md" :action="retry">
      <template #heading>Unable to Load Storage Options</template>
      <template #default>Something went wrong while loading storage plans.</template>
    </core-contextual-error-box>

    <u-tabs :items="tabs" v-else-if="hasRequiredProducts">
      <template #item="{ item }">
        <template v-if="item.value === 'one-time-payment'">
          <storage-product-selection-group
            @add-to-cart="addToCart"
            :is-adding-to-cart="effectiveIsAddingToCart"
            :products="oneTimePaymentProducts"
            :featured-sku="oneTimeFeaturedSku"
          />
          <p class="mt-1 text-xs text-gray-500" v-if="additionalText" v-html="additionalText" />
        </template>

        <template v-else>
          <storage-product-selection-group
            @add-to-cart="addToCart"
            :is-adding-to-cart="effectiveIsAddingToCart"
            :products="monthlyPaymentProducts"
            :featured-sku="monthlyFeaturedSku"
            is-monthly-payment
          />
          <p class="mt-1 text-xs text-gray-500" v-if="additionalText" v-html="additionalText" />
          <p class="mt-1 flex items-center justify-center gap-1 text-xs text-gray-500">
            <u-icon name="i-ri-information-2-line" class="shrink-0" aria-hidden="true" />
            <span>Your full storage capacity is available immediately.</span>
          </p>
        </template>
      </template>
    </u-tabs>
    <core-contextual-error-box v-else size="sm" class="border-2 border-gray-200 my-3 rounded-md">
      <template #heading>No Storage Products Available</template>
      <template #default>Please check back later or contact support.</template>
    </core-contextual-error-box>
  </client-only>

</template>

<script setup>
  const GROUP_SLUG = 'primary-storage-products';
  const FEATURED_STORAGE_SKUS = [
    'AO - 100GB storage',
    'AO - 100GB storage 24 mo'
  ];
  const SUPPLIED_FEATURED_STORAGE_SKUS = [
    'AO - 25GB storage',
    'AO - 25GB storage 24 mo'
  ];
  const props = defineProps({
    oneTimePaymentProducts: {
      type: Array,
      default: () => []
    },
    monthlyPaymentProducts: {
      type: Array,
      default: () => []
    },
    isAddingToCart: Boolean,
    additionalText: String,
    addToCart: Function,
    upsellTracking: Object
  });

  const tabs = [
    {
      label: 'One-Time Investment',
      value: 'one-time-payment',
    },
    {
      label: '24 Monthly Installments',
      value: 'monthly-payment'
    }
  ];

  const emit = defineEmits(['addToCart', 'error']);
  const hasSuppliedProducts = computed(() =>
    props.oneTimePaymentProducts.length && props.monthlyPaymentProducts.length
  );
  const pricingGroup = hasSuppliedProducts.value
    ? {
        products: ref([]),
        pending: ref(false),
        error: ref(null),
        retry: async () => {}
      }
    : usePricingGroup({
        groupSlug: GROUP_SLUG,
        server: false
      });
  const {
    products: fetchedProducts,
    pending,
    error,
    retry
  } = pricingGroup;
  const isLoading = computed(() => !hasSuppliedProducts.value && pending.value);

  const isAddingToCartInternal = ref(false);
  const effectiveIsAddingToCart = computed(() =>
    props.addToCart ? props.isAddingToCart : isAddingToCartInternal.value
  );
  const oneTimeFeaturedSku = computed(() =>
    oneTimePaymentProducts.value.find(product =>
      (hasSuppliedProducts.value ? SUPPLIED_FEATURED_STORAGE_SKUS : FEATURED_STORAGE_SKUS).includes(product.sku)
    )?.sku
  );
  const monthlyFeaturedSku = computed(() =>
    monthlyPaymentProducts.value.find(product =>
      (hasSuppliedProducts.value ? SUPPLIED_FEATURED_STORAGE_SKUS : FEATURED_STORAGE_SKUS).includes(product.sku)
    )?.sku
  );

  async function addToCart(product) {
    if (effectiveIsAddingToCart.value) return;

    trackUpsellCta({
      tracking: props.upsellTracking,
      product
    });

    if (props.addToCart) {
      await props.addToCart(product);
      return;
    }

    isAddingToCartInternal.value = true;
    try {
      await usePricingApi().addToCart({
        productId: product.id,
        promotionCode: product.promotionCode
      });
      window.location = EXTERNAL_URLS.cart;
    } catch {
      useErrorToast().add('Unable to add to cart at this time. Please try again later.');
    } finally {
      isAddingToCartInternal.value = false;
    }
  }

  const monthlySkus = [
    'AO - 25GB storage 24 mo',
    'AO - 50GB storage 24 mo',
    'AO - 100GB storage 24 mo',
  ];
  const oneTimeSkus = [
    'AO - 25GB storage',
    'AO - 50GB storage',
    'AO - 100 GB storage',
  ];
  const oneTimePaymentProducts = computed(() => hasSuppliedProducts.value
    ? props.oneTimePaymentProducts
    : (fetchedProducts.value?.filter(product => oneTimeSkus.includes(product.sku)) || [])
      .sort((a, b) => oneTimeSkus.indexOf(a.sku) - oneTimeSkus.indexOf(b.sku))
  );
  const monthlyPaymentProducts = computed(() => hasSuppliedProducts.value
    ? props.monthlyPaymentProducts
    : (fetchedProducts.value?.filter(product => monthlySkus.includes(product.sku)) || [])
      .sort((a, b) => monthlySkus.indexOf(a.sku) - monthlySkus.indexOf(b.sku))
  );
  const hasRequiredProducts = computed(() =>
    oneTimePaymentProducts.value.length && monthlyPaymentProducts.value.length
  );
  const fetchError = computed(() =>
    !hasSuppliedProducts.value &&
    !pending.value &&
    (
      Boolean(error.value) ||
      fetchedProducts.value != null && !hasRequiredProducts.value
    )
  );

  watch(fetchError, hasError => {
    if (hasError) {
      emit('error');
    }
  });
</script>
